import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Phone, Clock, ChevronDown, ChevronUp, MessageSquare, User, Mail, FileText, Tag, AlertTriangle, ThumbsUp, ThumbsDown, Minus } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import Logo from './Logo';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const INTENT_STYLES = {
  lead: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Lead' },
  appointment: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Appointment' },
  inquiry: { bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'Inquiry' },
  support: { bg: 'bg-purple-500/15', text: 'text-purple-400', label: 'Support' },
  spam: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Spam' },
  other: { bg: 'bg-gray-500/15', text: 'text-gray-400', label: 'Other' },
  unknown: { bg: 'bg-gray-500/15', text: 'text-gray-500', label: 'Pending' },
};

const SENTIMENT_ICON = {
  positive: <ThumbsUp className="w-3 h-3 text-emerald-400" />,
  neutral: <Minus className="w-3 h-3 text-gray-400" />,
  negative: <ThumbsDown className="w-3 h-3 text-red-400" />,
};

const CallHistory = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [callLogs, setCallLogs] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCallLogs();
  }, [token]);

  const fetchCallLogs = async () => {
    try {
      const res = await axios.get(`${API}/calls/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCallLogs(res.data);
    } catch (err) {
      console.log('Error fetching call logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const intentStyle = (intent) => INTENT_STYLES[intent] || INTENT_STYLES.unknown;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="call-history-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Logo linkTo="/dashboard" />
          <div className="h-6 w-px bg-white/10" />
          <h1 className="text-xl font-bold text-white">Call History & Transcripts</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20 text-gray-400">Loading call history...</div>
        ) : callLogs.length === 0 ? (
          <Card className="bg-gray-900/50 border-white/10">
            <CardContent className="py-16 text-center">
              <Phone className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2" data-testid="no-calls-msg">No calls yet</h3>
              <p className="text-gray-400 mb-6">Once your AI receptionist handles calls, they'll appear here with full transcripts.</p>
              <Button onClick={() => navigate('/phone-setup')} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="setup-phone-cta">
                Set Up Your Phone Number
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {callLogs.map((log) => {
              const style = intentStyle(log.intent);
              const expanded = expandedId === log.call_sid;

              return (
                <Card key={log.id || log.call_sid} className={`bg-gray-900/50 border-white/10 hover:border-white/20 transition-colors ${log.is_spam ? 'opacity-60' : ''}`} data-testid={`call-log-${log.call_sid}`}>
                  {/* Header row */}
                  <div
                    className="p-4 cursor-pointer flex items-center justify-between"
                    onClick={() => setExpandedId(expanded ? null : log.call_sid)}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${log.is_spam ? 'bg-red-500/20' : 'bg-purple-500/20'}`}>
                        {log.is_spam ? <AlertTriangle className="w-5 h-5 text-red-400" /> : <Phone className="w-5 h-5 text-purple-400" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-medium" data-testid="caller-number">{log.caller_number || 'Unknown'}</p>
                          {log.lead_data?.name && (
                            <span className="text-sm text-teal-400 flex items-center gap-1">
                              <User className="w-3 h-3" /> {log.lead_data.name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap mt-0.5">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {log.created_at ? new Date(log.created_at).toLocaleString() : 'N/A'}
                          </span>
                          <span className={`${style.bg} ${style.text} px-2 py-0.5 rounded text-xs font-medium`} data-testid="call-intent">
                            {style.label}
                          </span>
                          {log.sentiment && SENTIMENT_ICON[log.sentiment]}
                          {log.lead_captured && (
                            <span className="bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded text-xs font-medium" data-testid="lead-badge">
                              Lead Captured
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {log.transcript_entries?.length > 0 && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> {log.transcript_entries.length}
                        </span>
                      )}
                      {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {expanded && (
                    <CardContent className="pt-0 pb-4 border-t border-white/5">
                      {/* AI Summary */}
                      {log.summary && (
                        <div className="mt-3 mb-4 p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg" data-testid="call-summary">
                          <p className="text-xs text-blue-400 font-medium mb-1 flex items-center gap-1">
                            <FileText className="w-3 h-3" /> AI Summary
                          </p>
                          <p className="text-sm text-gray-300">{log.summary}</p>
                        </div>
                      )}

                      {/* Lead Data */}
                      {log.lead_captured && log.lead_data && (
                        <div className="mb-4 p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-lg" data-testid="lead-data">
                          <p className="text-xs text-emerald-400 font-medium mb-2 flex items-center gap-1">
                            <User className="w-3 h-3" /> Lead Information
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {log.lead_data.name && (
                              <div><span className="text-gray-500">Name:</span> <span className="text-white">{log.lead_data.name}</span></div>
                            )}
                            {log.lead_data.phone && (
                              <div><span className="text-gray-500">Phone:</span> <span className="text-white">{log.lead_data.phone}</span></div>
                            )}
                            {log.lead_data.email && (
                              <div><span className="text-gray-500">Email:</span> <span className="text-white">{log.lead_data.email}</span></div>
                            )}
                            {log.lead_data.service_interested && (
                              <div className="col-span-2"><span className="text-gray-500">Interested in:</span> <span className="text-white">{log.lead_data.service_interested}</span></div>
                            )}
                            {log.lead_data.notes && (
                              <div className="col-span-2"><span className="text-gray-500">Notes:</span> <span className="text-gray-300">{log.lead_data.notes}</span></div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action Items */}
                      {log.action_items?.length > 0 && (
                        <div className="mb-4 p-3 bg-amber-500/5 border border-amber-500/10 rounded-lg" data-testid="action-items">
                          <p className="text-xs text-amber-400 font-medium mb-1">Follow-up Actions</p>
                          <ul className="text-sm text-gray-300 space-y-1">
                            {log.action_items.map((item, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-amber-400 mt-0.5">-</span> {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Tags */}
                      {log.tags?.length > 0 && (
                        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="call-tags">
                          <Tag className="w-3 h-3 text-gray-500" />
                          {log.tags.map((tag, i) => (
                            <span key={i} className="bg-white/5 text-gray-400 px-2 py-0.5 rounded text-xs">{tag}</span>
                          ))}
                        </div>
                      )}

                      {/* Transcript */}
                      {log.transcript_entries?.length > 0 && (
                        <div className="space-y-3 mt-3" data-testid="transcript">
                          <p className="text-xs text-gray-500 font-medium">Full Transcript</p>
                          {log.transcript_entries.map((entry, i) => (
                            <div key={i} className="space-y-2">
                              <div className="flex gap-3">
                                <span className="text-xs text-gray-500 w-14 flex-shrink-0 pt-1">Caller:</span>
                                <p className="text-gray-300 text-sm bg-white/5 px-3 py-2 rounded-lg flex-1">{entry.text}</p>
                              </div>
                              <div className="flex gap-3">
                                <span className="text-xs text-purple-400 w-14 flex-shrink-0 pt-1">Mia:</span>
                                <p className="text-gray-200 text-sm bg-purple-500/10 px-3 py-2 rounded-lg flex-1">{entry.response}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default CallHistory;
